import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Spinner, Callout, Intent } from "@blueprintjs/core";

import { Colors, actuatorDispositionStyle } from "design/colors";
import { useReset } from "hooks/useReset";
import { usePlayback } from "hooks/usePlayback";
import {
  Device,
  MIN_FW_VERSION,
  useDevices,
} from "hooks/useDevices";
import { SmFwBuildType } from "utils/common-types";
import { versionLessThan, buildTypeIntToString, downloadData, filenameWithTimestamp } from "utils/common";
import {
  flashFirmware,
  backupFirmware,
  writeFlashConfig,
  preCheckFileAndDevice,
  PreCheckProblem,
  FlashInfo,
} from "connections/controller";
import { FlashAction, ProgressCb, fileToBytes } from "connections/flashing/common";
import { supportsDFU } from "connections/flashing/dfu";
import { Button } from "components/Button";
import { CStoreDisplay } from "components/CStoreDisplay";
import { CStore } from "cstore/cstore";
import { CStoreEditor } from "./screens/CStoreEditor";
import { SubActuatorStep } from "connections/pscb";
import { ActuatorInfo } from "utils/slnt-file";
import "./screens/Config.css";
import { UnsupportedBrowser } from "components/UnsupportedBrowser";
import { HandshakeStatus, mcuErrToStr } from "connections/auth/handshake";

const MIN_FIRMWARE_FILE_SIZE =    65536; // 64 KiB
const MAX_UPLOAD_FILE_SIZE   = 33554432; // 32 MiB

type FlashMgmtState = {
  isFlashOp: true;
  device: HIDDevice;
  deviceFlashInfo?: FlashInfo,
  file: File;
  fileBytes: Uint8Array;
} | {
  isFlashOp: false; // backup
  device: HIDDevice;
}

const ControllerItem = (props: {
  device: Device;
  setFlashMgmtState: (state: FlashMgmtState | undefined) => void;
  mainColor: string;
  mainAction: () => void;
  mainText: string;
  mainActionDisabled: boolean;
  setFlashAction: (type: FlashAction) => void;
  backupFirmware: (device: HIDDevice) => void;
  setFlashProgress: ProgressCb;
  setCStoreDisplayDevice: ( device?: Device ) => void;
  showFlashingError: (message: string) => void;
  editCStore: () => void;
}) => {
  const flashFirmwareInputRef = useRef<HTMLInputElement>(null);
  const updateConfigInputRef = useRef<HTMLInputElement>(null);
  const {
      device,
      setFlashMgmtState,
      setFlashAction,
      backupFirmware,
      setFlashProgress,
      setCStoreDisplayDevice,
      showFlashingError,
      editCStore,
  } = props;

  let firmwareSubText;
  if ( device.initialized ) {
    if ( versionLessThan(device.fwVersion, MIN_FW_VERSION) ) {
      firmwareSubText = `>= ${MIN_FW_VERSION.join(".")} required`;
    } else if ( device.needToUpdate ) {
      // General update notice without specifying a version
      firmwareSubText = "Update required";
    }
  }

  const handleFlashFirmwareInputChange = useCallback(
    async (e: React.FormEvent<HTMLInputElement>) => {
      try {
        const files = e.currentTarget.files;
        if (files === null || files.length === 0) {
          return;
        }

        const file = files[0]; // Note: no `multiple` attribute on input element

        // Prevent flashing files that are too small (cstore bin files, etc.)
        // Also avoid loading very large files into memory.
        if (file.size < MIN_FIRMWARE_FILE_SIZE) {
          showFlashingError(
            "Error: file is too small -- it may be a cstore bin file. " +
            "Please select a proper firmware file."
          );
          return;
        } else if (file.size > MAX_UPLOAD_FILE_SIZE) {
          showFlashingError(
            "Error: file is too large. Please select a proper firmware file."
          );
          return;
        }

        setFlashAction(
          (file.name.endsWith(".cfw"))
            ? FlashAction.FlashExeAndConfig
            : FlashAction.FlashExecutable);

        const fileBytes = await fileToBytes( file );
        setFlashMgmtState({
          isFlashOp: true,
          device: device.handle,
          deviceFlashInfo: device.initialized ? device?.flashInfo : undefined,
          file,
          fileBytes,
        });
      } finally {
        if (flashFirmwareInputRef.current !== null) {
          flashFirmwareInputRef.current.value = "";
        }
      }
    },
    [device, setFlashMgmtState, setFlashAction, showFlashingError]
  );

  const handleUpdateConfigInputChange = useCallback(
    async (e: React.FormEvent<HTMLInputElement>) => {
      try {
        const files = e.currentTarget.files;
        if (files === null || files.length === 0) {
          return;
        }

        const file = files[0]; // Note: no `multiple` attribute on input element

        // Avoid loading very large files into memory.
        if (file.size > MAX_UPLOAD_FILE_SIZE) {
          showFlashingError(
            "Error: file is too large. Please select a proper config or CFW file."
          );
          return;
        }

        setFlashAction(FlashAction.FlashConfig);

        const fileBytes = await fileToBytes( file );

        setFlashMgmtState({
          isFlashOp: true,
          device: device.handle,
          deviceFlashInfo: device.initialized ? device?.flashInfo : undefined,
          file,
          fileBytes,
        });
      } finally {
        if (updateConfigInputRef.current !== null) {
          updateConfigInputRef.current.value = "";
        }
      }
    },
    [device, setFlashMgmtState, setFlashAction, showFlashingError]
  );

  const downloadCstoreBin = useCallback( () => {
    const cstoreBinData = device.initialized && device?.cstore?.export();
    if ( !cstoreBinData ) return;

    // Descriptive file name
    const productName = device.handle.productName.replace("Salient Motion", "")
                              .replace(/ /g, "").toLowerCase().replace(/controller/g, "");
    const baseName = filenameWithTimestamp( `cstore-${ productName }` );

    // File dialog
    downloadData( `${ baseName }.bin`, cstoreBinData );
  }, [ device ]);

  // Devices are always initialized, but TypeScript doesn't know that
  if ( !device.initialized ) return <></>;

  const buildTypeString: string = buildTypeIntToString(device.buildInfo.buildType);
  const buildYear = device.buildInfo.year;
  const buildMonth = device.buildInfo.month;
  const buildDay = device.buildInfo.day;
  const buildIncrement = device.buildInfo.increment;
  const isNotReleaseBuild = ( device.buildInfo.buildType !== SmFwBuildType.RELEASE );

  const isCStorePresent = device.cstore !== undefined;
  const cstoreText = "Config: " +
      ( isCStorePresent ? `${ ( device.cstore?.storedTags.length ?? 0 ) - 1 } items` : device.cstoreErr?.toString() );

  let authSuccessful = false;
  let authError = false;
  let authStatusText = "(No account keypair)";
  let authAdditionalText: string | undefined;
  if ( device.handshaker !== undefined ) {
    switch ( device.handshaker.status ) {
      case HandshakeStatus.NotSupported:
        authStatusText = "Auth handshake not supported; old firmware?";
        break;
      case HandshakeStatus.Delaying:
        authStatusText = "Warm up delay...";
        break;
      case HandshakeStatus.InProgress:
        authStatusText = "In progress...";
        break;
      case HandshakeStatus.Successful:
        authStatusText = "VERIFIED ✅";
        authSuccessful = true;
        break;
      case HandshakeStatus.Failed:
        authStatusText = "⚠️  FAIL ⚠️ ";
        authError = true;
        if ( device.handshaker.mcuErr !== undefined ) {
            authAdditionalText = mcuErrToStr( device.handshaker.mcuErr );
        } else if ( device.handshaker.err !== undefined ) {
            authAdditionalText = device.handshaker.err.msg;
        }
        break;
      default:
    }
  }

  // Sub-actuator manager status display (similar to handshaker status)
  let subActuatorsSuccessful = false;
  let subActuatorsError = false;
  let subActuatorsStatusText = "";
  let subActuatorsAdditionalText: string | undefined;
  if ( device.subActuatorManager !== undefined ) {
    switch ( device.subActuatorManager.state.step ) {
      case SubActuatorStep.Warmup:
        subActuatorsStatusText = "Warm up...";
        break;
      case SubActuatorStep.OK: {
        const actuatorCount = Object.keys(device.subActuatorManager.state.actuators).length;
        subActuatorsStatusText = `${actuatorCount} sub-device${actuatorCount !== 1 ? 's' : ''} detected`;
        subActuatorsSuccessful = true;
        break;
      }
      case SubActuatorStep.Error:
        subActuatorsStatusText = "⚠️ Sub-device Error ⚠️";
        subActuatorsError = true;
        subActuatorsAdditionalText = typeof device.subActuatorManager.state.err === 'string' 
          ? device.subActuatorManager.state.err 
          : device.subActuatorManager.state.err?.message;
        break;
    }
  }

  const gitHashText = `${device.buildInfo.gitCommitSHA1.toString(16).padStart(8, "0").slice(0, 7)}`;
  return (
    <>
      <div
        style={{
          flex: "1 1 0px",
          minWidth: 650,
          display: "flex",
          padding: 16,
          color: Colors.WHITE,
          fontSize: 16,
          backgroundColor: Colors.DARK_GREEN,
          boxShadow: "1px 2px 9px #222222",
          marginBottom: 24,
          borderRadius: 8,
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: "bold", marginBottom: 2 }}>
              {device.handle.productName.replace("Salient Motion", "")}
            </div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              {device.mcuSerialNum}
            </div>
            <div style={{ fontSize: 18, opacity: 0.90, marginTop: 10 }}>
              { isNotReleaseBuild
              ? `${buildTypeString} ${buildYear}.${buildMonth}.${buildDay}.${buildIncrement}`
              : undefined}
            </div>
            <div style={{ fontSize: 18, opacity: 0.90, marginTop: 10, color: Colors.RED }}>
              { device.buildInfo.dirty && "Compiled with uncommitted changes!" }
            </div>
          </div>
          <div style={{ fontSize: 18 }}>
            Auth Status:{" "}
            <span style={{
                color: authSuccessful ? Colors.WHITE : (
                    authError ? Colors.DARK_RED : Colors.YELLOW
                ),
              fontWeight: "bold",
            }}>
              {authStatusText}
            </span>
          </div>
          {
            authAdditionalText !== undefined && (
              <div style={{ fontSize: 14, width: 250 }}>
                <span style={{ fontWeight: "bold" }}>Additional Auth Status:</span><br />
                {authAdditionalText}
              </div>
            )
          }
          {subActuatorsStatusText &&
            (<div style={{ fontSize: 18 }}>
              Sub-devices:{" "}
              <span style={{
                  color: subActuatorsSuccessful ? Colors.WHITE : (
                      subActuatorsError ? Colors.DARK_RED : Colors.YELLOW
                  ),
                fontWeight: "bold",
              }}>
                {subActuatorsStatusText}
              </span>
            </div>)
          }
          {subActuatorsAdditionalText !== undefined && (
            <div style={{ fontSize: 14, width: 250 }}>
              <span style={{ fontWeight: "bold" }}>Sub-devices Error Details:</span><br />
              {subActuatorsAdditionalText}
            </div>
          )}
            <div style={{ height: 8 }} />
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}>
            <div style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}>
              <Button
                backgroundColor={isCStorePresent ? Colors.WHITE : Colors.DARK_RED}
                textColor={isCStorePresent ? Colors.BLACK : Colors.WHITE }
                onClick={() => setCStoreDisplayDevice( device )}
                disabled={!isCStorePresent}
              >
                <span style={{ fontWeight: isCStorePresent ? "normal" : "bold" }}>
                  {cstoreText}
                </span>
              </Button>
              <FontAwesomeIcon
                icon={ faDownload }
                style={{ padding: "0px 6px", cursor: "pointer" }}
                onClick={ () => { downloadCstoreBin() } }
              />
            </div>
            <div style={{ height: 4 }} />
            <Button
              backgroundColor={Colors.WHITE}
              textColor={Colors.BLACK}
              onClick={editCStore}
            >
              <FontAwesomeIcon
                icon={ faPenToSquare }
                style={{ padding: "0px 6px", cursor: "pointer" }}
              />
              Edit Config
            </Button>
          </div>
        </div>

        <div style={{ width: 128 }} />

        <div>
          { /* Connect / Disconnect */ }
          <Button
            backgroundColor={props.mainColor}
            textColor={Colors.WHITE}
            onClick={props.mainAction}
            disabled={props.mainActionDisabled}
          >
            {props.mainText}
          </Button>
          <div style={{ height: 8 }} />
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            onClick={() => flashFirmwareInputRef.current?.click()}
          >
            <input
              ref={flashFirmwareInputRef}
              style={{ display: "none" }}
              type="file"
              onInput={handleFlashFirmwareInputChange}
              accept=".bin, .cfw"
            />
            Update Firmware
          </Button>
          <div style={{ height: 8 }} />
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            onClick={() => updateConfigInputRef.current?.click()}
          >
            <input
              ref={updateConfigInputRef}
              style={{ display: "none" }}
              type="file"
              onInput={handleUpdateConfigInputChange}
              accept=".bin"
            />
            Update Config
          </Button>
          <div style={{ height: 8 }} />
          <Button
            backgroundColor={Colors.WHITE}
            textColor={Colors.BLACK}
            onClick={() => {
                if ( !device.initialized ) return;
                // ToDo: these should probably be set in _backupFirmware in Controllers
                setFlashAction(FlashAction.BackupFirmware);
                setFlashMgmtState({ isFlashOp: false, device: device.handle });
                setFlashProgress(0);
                backupFirmware(device.handle);
              }
            }
            disabled={true}
          >
            Backup Firmware
          </Button>
          <div style={{ fontSize: 14, marginTop: 2, textAlign: "center" }}>
          Current: v{device.fwVersion.join(".")}
          </div>
          <div style={{ fontSize: 14, opacity: 0.90, textAlign: "center" }}>
            { gitHashText }
          </div>
          {firmwareSubText && (
            <div style={{ fontSize: 12, marginTop: 2, color: Colors.YELLOW }}>
              {firmwareSubText}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const SubDeviceItem = (props: { subDevice: ActuatorInfo }) => {
  const { subDevice } = props;
  return (
    <div
      style={{
        display: "flex",
        padding: "12px 16px",
        marginLeft: 32,
        marginBottom: 12,
        color: Colors.WHITE,
        fontSize: 14,
        backgroundColor: Colors.DARK_GREEN,
        boxShadow: "1px 2px 6px #222222",
        borderRadius: 6,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span style={{ ...actuatorDispositionStyle, fontSize: 16 }}>
        {subDevice.disposition}
      </span>
      <span style={{ opacity: 0.8, fontSize: 14 }}>
        Node ID: {subDevice.nodeId}
      </span>
    </div>
  );
};

export const Controllers = (
  props: { editCStore: () => void },
) => {
  const { editCStore } = props;
  const reset = useReset();
  const { devices, connectedDevice, connect, pairDevice } = useDevices();
  const { live } = usePlayback();
  const [flashMgmtState, setFlashMgmtState] = useState<
    FlashMgmtState | undefined
  >();
  const [flashProgress, setFlashProgress] = useState<number | undefined>();
  const [flashAction, setFlashAction] = useState<FlashAction | undefined>();
  const [cstoreDisplayDevice, setCStoreDisplayDevice] = useState<Device | undefined>();
  const [flashingErrorMessage, setFlashingErrorMessage] = useState<string | undefined>();

  // Ensure the dialog is closed when the connected device goes away.
  useEffect(() => {
      if ( connectedDevice === undefined || !connectedDevice.initialized ) {
          setCStoreDisplayDevice(undefined);
      }
  }, [ connectedDevice ]);

  const unsupportedBrowser = ( navigator['hid'] === undefined );

  const _backupFirmware = async (device: HIDDevice) => {
    try {
      const firmwareData = await backupFirmware(device, setFlashProgress);
      downloadData( "firmware.bin", firmwareData );
    } finally {
      setFlashMgmtState(undefined);
      setFlashAction(undefined);
      setFlashProgress(undefined);
    }
  };

  const showFlashingError = useCallback((message: string) => {
    setFlashingErrorMessage(message);
  }, []);

  const flashingErrorDialog = useMemo(() => {
    if (flashingErrorMessage === undefined) {
      return null;
    }

    return (
      <Dialog
        style={{
          backgroundColor: Colors.DARK_GREEN,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 500,
        }}
        isOpen={true}
        onClose={() => setFlashingErrorMessage(undefined)}
      >
        <div
          style={{
            color: Colors.RED,
            fontSize: 20,
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          Firmware Flash Error
        </div>
        <div
          style={{
            color: Colors.WHITE,
            fontSize: 16,
            marginBottom: 24,
          }}
        >
          {flashingErrorMessage}
        </div>
        <Button
          backgroundColor={Colors.BUTTON_GREEN}
          textColor={Colors.WHITE}
          style={{width: "50px"}}
          onClick={() => setFlashingErrorMessage(undefined)}
        >
          OK
        </Button>
      </Dialog>
    );
  }, [flashingErrorMessage]);

  let flashDescription: string | undefined;
  if ( flashMgmtState?.isFlashOp && isDefined( flashAction ) ) {
    switch ( flashAction ) {
      case FlashAction.FlashConfig: {
        flashDescription = "Device configuration will be updated.";
        break;
      }
      case FlashAction.FlashExeAndConfig: {
        flashDescription = "Device firmware and configuration will both be updated.";
        break;
      }
      case FlashAction.FlashExecutable: {
        flashDescription = "Device firmware will be updated.";
        break;
      }
    }
  }

  const preFlashMessages = useMemo(() => {
    if ( flashAction === undefined ) return [];
    const fileBytes = flashMgmtState?.isFlashOp && flashMgmtState?.fileBytes;
    if ( !fileBytes ) return [];
    const { productId, productName } = flashMgmtState.device;
    const problems = preCheckFileAndDevice( fileBytes, flashAction, flashMgmtState.deviceFlashInfo, productId, productName );
    return problems.map( ( problem: PreCheckProblem ) => {
      if ( problem === PreCheckProblem.CrcMismatch ) {
        return (
          <Callout intent={ Intent.DANGER } key={"preFlashMessage-"+problem} >
            Corrupt firmware image detected! It is strongly recommended that you press Cancel.
          </Callout>
        );
      }
      if ( problem === PreCheckProblem.NoCrcData ) {
        return (
          <Callout intent={ Intent.WARNING } key={"preFlashMessage-"+problem} >
            The firmware image has no CRC (Cyclic Redundancy Check). It is not possible to detect corruption.
          </Callout>
        );
      }
      if ( problem === PreCheckProblem.NoCStoreSignature ) {
        return (
          <Callout intent={ Intent.DANGER } key={"preFlashMessage-"+problem} >
            No configuration store was detected in the file! It is strongly recommended that you press Cancel.
          </Callout>
        );
      }
      if ( problem === PreCheckProblem.UnknownDeviceProductCode ) {
        return (
          <Callout intent={ Intent.WARNING } key={"preFlashMessage-"+problem} >
            The product code of the connected device could not be determined.
          </Callout>
        );
      }
      if ( problem === PreCheckProblem.CStoreProductCodeMismatch ) {
        return (
          <Callout intent={ Intent.DANGER } key={"preFlashMessage-"+problem} >
            The product code of the connected device differs from the configuration in the file you uploaded!
            It is strongly recommended that you press Cancel.
          </Callout>
        );
      }
      if ( problem === PreCheckProblem.DFUDeviceMissingSomeFlashInfo ) {
        return (
          <Callout intent={Intent.WARNING} key={"preFlashMessage-dfu-sector-size"+problem} >
            Configuration will be lost during this update because the device firmware doesn't provide
            the information needed to preserve it (may be older firmware that lacks the support).
          </Callout>
        );
      }
      return ( <span key={"preFlashMessage-unknown"}>Unknown PreCheckProblem</span> );
    } );
  }, [ flashAction, flashMgmtState ]);

  const updateDialog = useMemo(() => {
    if (flashMgmtState === undefined) {
      return null;
    }

    const { device } = flashMgmtState;

    const flash = async () => {
      if ( !flashMgmtState.isFlashOp ) throw new Error( "expected flash operation, not backup/download" );
      const { device, file } = flashMgmtState;

      setFlashProgress(0);
      try {
        if ( flashAction === undefined ) throw new Error( 'unknown flashAction' );
        await flashFirmware(device, file, flashAction, setFlashProgress);
      } finally {
        setFlashMgmtState(undefined);
        setFlashAction(undefined);
        setFlashProgress(undefined);
      }
    };

    const _writeConfig = async () => {
      if ( !flashMgmtState.isFlashOp ) throw new Error( "expected flash operation, not backup/download" );
      const { device, file } = flashMgmtState;

      setFlashProgress(0);
      try {
        await writeFlashConfig(device, file, setFlashProgress);
      } finally {
        setFlashMgmtState(undefined);
        setFlashAction(undefined);
        setFlashProgress(undefined);
      }
    };

    const progressSpinner =
      flashProgress === undefined ? null : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 32,
          }}
        >
          <Spinner intent="success" value={flashProgress} />
          <div style={{ height: 8 }} />
          <div style={{ color: Colors.WHITE }}>
            {Math.floor(flashProgress * 100)}%
          </div>
        </div>
      );

    return (
      <Dialog
        style={{
          backgroundColor: Colors.DARK_GREEN,
          padding: 16,
          display: "flex",
          flexDirection: "column",
          whiteSpace: "nowrap",
          width: "fit-content",
        }}
        isOpen={true}
        onClose={
          flashProgress === undefined
            ? () => setFlashMgmtState(undefined)
            : undefined
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            margin: "16px 32px 32px 32px",
          }}
        >
          <div
            style={{
              color: Colors.WHITE,
              fontSize: 20,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {
              flashMgmtState.isFlashOp ?
              (<>
                {flashProgress === undefined ? "Update" : "Updating"}&nbsp;
                <span style={{ fontWeight: "bold" }}>{device.productName}</span>
                &nbsp;with&nbsp;
                <span style={{ fontWeight: "bold" }}>{flashMgmtState.file.name}</span>
                {flashProgress === undefined && "?"}
              </>) : "Backing up firmware"
            }
          </div>
        </div>
        <div
          style={{
            color: Colors.WHITE,
            fontSize: 16,
            display: "flex",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          {flashDescription}
        </div>
        {(supportsDFU(device) && isDefined(flashAction) &&
          [FlashAction.FlashExecutable, FlashAction.FlashExeAndConfig].includes(flashAction)) ? (
          <div
            style={{
              color: Colors.YELLOW,
              fontSize: 16,
              display: "flex",
              justifyContent: "center",
              marginTop: 16,
            }}
          >
            After clicking "Flash", you'll be asked to select a device.
            Select&nbsp;
            <span style={{ fontWeight: "bold" }}>DFU in FS Mode</span>.
          </div>
        ) : null}
        {...preFlashMessages}
        {progressSpinner === null && flashAction !== undefined && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                backgroundColor={Colors.WHITE}
                textColor={Colors.BLACK}
                onClick={() => setFlashMgmtState(undefined)}
              >
                Cancel
              </Button>
            </div>
            <div style={{ width: 8 }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              {[FlashAction.FlashExecutable, FlashAction.FlashExeAndConfig].includes(flashAction) && (
                <Button
                  backgroundColor={Colors.BUTTON_GREEN}
                  textColor={Colors.WHITE}
                  onClick={flash}
                >
                  Update Firmware
                </Button>
              )}
              {flashAction === FlashAction.FlashConfig && (
                <Button
                  backgroundColor={Colors.BUTTON_GREEN}
                  textColor={Colors.WHITE}
                  onClick={_writeConfig}
                >
                  Update Config
                </Button>
              )}
            </div>
          </div>
        )}
        { progressSpinner }
      </Dialog>
    );
  }, [flashProgress, setFlashProgress, flashMgmtState, flashAction, preFlashMessages, flashDescription]);

  const deviceEls = useMemo(() => {
    if (devices === undefined) {
      return null;
    }

    if (devices.length === 0) {
      return <div style={{ color: Colors.WHITE }}>No controllers found.</div>;
    }

    return devices.map((device, index) => {
      const isConnected =
        connectedDevice?.initialized &&
        device.initialized &&
        connectedDevice.mcuSerialNum === device.mcuSerialNum;
      const disabled =
        !device.initialized ||
        versionLessThan(device.fwVersion, MIN_FW_VERSION);

      // Get sub-devices if available
      const subDevices: ActuatorInfo[] = 
        device.initialized && 
        device.subActuatorManager?.state.step === SubActuatorStep.OK
          ? Object.values(device.subActuatorManager.state.actuators)
          : [];

      return (
        <React.Fragment key={"device-group-" + index}>
          <ControllerItem
            device={device}
            setFlashMgmtState={setFlashMgmtState}
            mainColor={isConnected ? Colors.RED : Colors.BUTTON_GREEN}
            mainAction={
              isConnected
                ? reset
                : async () => {
                    await reset();
                    connect(device);
                    live();
                  }
            }
            mainActionDisabled={disabled}
            mainText={isConnected ? "Disconnect" : "Connect"}
            setFlashAction={setFlashAction}
            setFlashProgress={setFlashProgress}
            backupFirmware={_backupFirmware}
            setCStoreDisplayDevice={setCStoreDisplayDevice}
            showFlashingError={showFlashingError}
            editCStore={editCStore}
          />
          {subDevices.map((subDevice) => (
            <SubDeviceItem 
              key={`subdevice-${device.handle.productId}-${subDevice.nodeId}`}
              subDevice={subDevice}
            />
          ))}
        </React.Fragment>
      );
    });
  }, [connectedDevice, devices, connect, reset, live, setCStoreDisplayDevice, showFlashingError, editCStore]);

  const cstoreDisplay = useMemo(() => {
      if ( cstoreDisplayDevice && cstoreDisplayDevice.initialized && cstoreDisplayDevice.cstore ) {
          return (
              <CStoreDisplay
                  cstore={cstoreDisplayDevice.cstore}
                  onClose={() => setCStoreDisplayDevice(undefined)}
              />
          );
      }
  }, [cstoreDisplayDevice]);

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 32,
      }}
    >
      {updateDialog}
      {cstoreDisplay}
      {flashingErrorDialog}
      <div
        style={{
          fontSize: 24,
          fontWeight: "bold",
          color: Colors.WHITE,
          marginBottom: 32,
        }}
      >
        <div style={{ marginBottom: 16 }}>Paired Controllers</div>
        <Button
          backgroundColor={Colors.BUTTON_GREEN}
          textColor={Colors.WHITE}
          disabled={ unsupportedBrowser }
          onClick={ unsupportedBrowser ? undefined : pairDevice }
        >
          Pair
        </Button>
      </div>
      { unsupportedBrowser && <UnsupportedBrowser /> }
      <div style={{ display: "flex", flexDirection: "column" }}>
        {deviceEls}
      </div>
    </div>
  );
};

enum Screens {
  Main,
  CStoreEditor,
}

export const Devices = () => {
  const { connectedDevice } = useDevices();
  const [screen, setScreen] = useState<Screens>(Screens.Main);
  const cstore: CStore | undefined = connectedDevice?.initialized ? connectedDevice.cstore : undefined;
  const exit = useCallback(() => {
    setScreen(Screens.Main);
  }, []);

  if (screen === Screens.CStoreEditor) {
    if ( !isDefined( cstore ) ) {
      exit();
      return null;
    }
    return (
      <CStoreEditor cstore={ cstore } exit={ exit } />
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <Controllers
        editCStore={() => setScreen(Screens.CStoreEditor)}
      />
    </div>
  );
};
